/**
 * src/lib/org.ts
 * ---------------
 * Single-organization mode.
 *
 * This deployment serves exactly ONE organization. The id is fixed at build
 * time by VITE_ORG_ID and must match the Admin Console's HELIX_ORG_ID —
 * the two apps share a Mongo database and Redis instance, and every live
 * record is keyed by org:
 *
 *   Mongo  conversation_logs / alert_events   { org_id: … }
 *   Redis  live_events:{org_id}  ·  alerts:{org_id}
 *
 * If the two sides disagree the Admin Live Wall subscribes to a channel the
 * Customer app never publishes on, and no conversation ever appears.
 *
 * Deliberately NOT read from the URL or localStorage. A `?org_id=` override
 * let any visitor point the widget at another tenant, leaked the id into
 * every navigation, and let a stale cached value outlive a redeployment.
 */

/** Fallback used when VITE_ORG_ID is unset. Keep in sync with HELIX_ORG_ID. */
const FALLBACK_ORG_ID = 'f7727934-0221-4489-83fe-74f79157479b'

const ORG_STORAGE_KEY = 'helix_org_id'

/** The one organization this deployment serves. */
export function getCurrentOrgId(): string {
  const configured = (import.meta.env.VITE_ORG_ID as string | undefined)?.trim()
  return configured || FALLBACK_ORG_ID
}

/**
 * Mirror the org into localStorage purely so debugging tools and the previous
 * cache-key format keep working. Nothing reads it back as a source of truth.
 */
export function rememberOrgId(orgId = getCurrentOrgId()): void {
  try {
    localStorage.setItem(ORG_STORAGE_KEY, orgId)
  } catch {
    /* private mode / quota — the org is a constant anyway */
  }
}

export function getAppearanceCacheKey(orgId: string): string {
  return `helix-appearance-cache:${orgId}`
}
